"use client";

import { useCallback, useMemo } from "react";
import { BettingOdds } from "fansunited-sports-ui";
import type { BookmakerOdds, OddsData } from "@/features/odds/types";
import { useBetslip } from "@/features/betslip/useBetslip";
import { useOddsDisplay } from "@/features/odds/useOddsDisplay";

interface LivescoreOddsProps {
    bookmakerOdds: BookmakerOdds;
    isMobile: boolean;
    matchId?: string;
    /** ISO 8601 kickoff timestamp — passed to addOddsSelection so persisted items expire 3 h after kickoff */
    kickoffAt?: string;
}

export function LivescoreOdds({ bookmakerOdds, isMobile, matchId, kickoffAt }: LivescoreOddsProps) {
    const { addOddsSelection, canAddOdds } = useBetslip();
    const tenantOdds = useOddsDisplay();

    const shouldInterceptOdds = canAddOdds && !!matchId;

    const handleOddsClick = useCallback(
        (odd: OddsData) => {
            if (!shouldInterceptOdds || !matchId) return;
            addOddsSelection(matchId, "FT_1X2", odd.label, kickoffAt);
        },
        [shouldInterceptOdds, matchId, addOddsSelection, kickoffAt]
    );

    const processedOdds = useMemo(() => {
        if (!shouldInterceptOdds) {
            return bookmakerOdds.odds;
        }

        return bookmakerOdds.odds.map((odd) => ({
            ...odd,
            onClick: () => handleOddsClick(odd),
            url: undefined,
        }));
    }, [bookmakerOdds.odds, shouldInterceptOdds, handleOddsClick]);

    return (
        <BettingOdds
            odds={processedOdds}
            operator={bookmakerOdds.operator}
            styles={{
                container: "p-0 w-full",
                operatorContainer: "p-0 w-16",
                oddsContainer: "px-2 py-1.5 odds-button",
                // Mobile: keep deliberate 3000ms flash for narrow rows.
                // Desktop: defer to tenant setting (default = no flash).
                ...(isMobile
                    ? { mode: "flash" as const, flashInterval: 3000 }
                    : { mode: tenantOdds.displayMode, flashInterval: tenantOdds.flashInterval }),
            }}
        />
    );
}
